import * as React from "react";
import { Header } from "src/components/Header";
import { Page } from "src/components/Page";
import { trpc } from "src/trpc";
import { CategoriesEditor } from "./CategoriesEditor";
import type { CategoryNode } from "@iterate/common/types/shared";
import { useImmer } from "use-immer";
import { v4 as uuid } from "uuid";
import { Tree } from "src/utils/Tree";
import { CategoryForm } from "./CategoryForm";
import { PlusIcon } from "lucide-react";
import styled from "styled-components";
import { produce } from "immer";

interface CategoryModal {
  mode: "create" | "edit";
  categoryId: string;
}

function createEmptyCategory(): CategoryNode {
  return {
    id: uuid(),
    name: "",
    budget: null,
    children: [],
  };
}

export function Categories() {
  const { data: initialCategories } = trpc.getCategories.useQuery();
  const [categories, setCategories] = useImmer<CategoryNode[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const { mutateAsync: saveCategories } = trpc.saveCategories.useMutation();
  const [categoryModal, setCategoryModal] = React.useState<CategoryModal | null>(null);

  React.useEffect(() => {
    if (initialCategories && !loaded) {
      setCategories(initialCategories);
      setLoaded(true);
    }
  }, [initialCategories, loaded]);

  const updateCategories = (fn: (categories: CategoryNode[]) => void) => {
    const newCategories = produce(categories, fn);
    setCategories(newCategories);
    saveCategories({ categories: newCategories });
  };

  const createCategory = (parentId?: string) => {
    const category = createEmptyCategory();
    setCategories(produce((categories) => Tree.insertNode(categories, category, parentId)));
    setCategoryModal({ mode: "create", categoryId: category.id });
  };

  const updateModalCategory = (category: CategoryNode) => {
    setCategories(produce((categories) => Tree.updateNode(categories, category)));
  };

  const createChildCategory = (category: CategoryNode) => {
    createCategory(category.id);
  };

  const editCategory = (category: CategoryNode) => {
    setCategoryModal({ mode: "edit", categoryId: category.id });
  };

  const saveModalCategory = () => {
    saveCategories({ categories });
    setCategoryModal(null);
  };

  const cancelEditCategory = () => {
    if (category && categoryModal?.mode === "create") {
      updateCategories((categories: CategoryNode[]) => Tree.deleteNode(categories, category.id));
    }
    setCategoryModal(null);
  };

  const category = categoryModal && Tree.getNodeById(categories, categoryModal.categoryId);

  return (
    <Page>
      <Header title="Categories">
        <Header.Button icon={PlusIcon} onClick={() => createCategory()}>
          New Category
        </Header.Button>
      </Header>
      <CategoryList>
        <CategoriesEditor
          categories={categories}
          onChangeCategories={updateCategories}
          onEditCategory={editCategory}
          onCreateChildCategory={createChildCategory}
        />
      </CategoryList>
      {category && (
        <CategoryForm
          category={category}
          onChange={updateModalCategory}
          onSave={saveModalCategory}
          onCancel={cancelEditCategory}
        />
      )}
    </Page>
  );
}

const CategoryList = styled.div`
  @media (max-width: 600px) {
    margin: 0 5px;
  }
`;
